import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { Link } from 'react-router-dom';
import videoFile from './practice.mp4';
import '../PracticeArea/Areavideo.css';

const PRACTICE_AREAS = [
  { label: 'Family Law',        icon: '⚖' },
  { label: 'Criminal Defense',  icon: '🛡' },
  { label: 'Corporate Law',     icon: '🏛' },
  { label: 'Immigration',       icon: '🌐' },
  { label: 'Civil Litigation',  icon: '📜' },
  { label: 'Real Estate',       icon: '🏢' },
];

const STATS = [
  { num: '25+',    label: 'Years' },
  { num: '1,200+', label: 'Cases' },
  { num: '97%',    label: 'Success' },
];

const Areavideo = () => {
  const containerRef  = useRef(null);
  const videoRef      = useRef(null);
  const slashRef      = useRef(null);
  const glowRef       = useRef(null);
  const contentRef    = useRef(null);
  const headingRef    = useRef(null);
  const subRef        = useRef(null);
  const tagsRef       = useRef(null);
  const statsRef      = useRef(null);
  const ctaRef        = useRef(null);
  const eyebrowRef    = useRef(null);
  const lineRef       = useRef(null);
  const particlesRef  = useRef(null);
  const videoWrapRef  = useRef(null);

  useEffect(() => {
    /* ── Autoplay video ── */
    videoRef.current?.play().catch(() => {});

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'expo.out' } });

      /* 1. Video panel slides in from left */
      tl.fromTo(videoWrapRef.current,
        { x: '-100%', opacity: 0 },
        { x: '0%', opacity: 1, duration: 1.4 },
        0
      );

      /* 2. Diagonal slash reveals */
      tl.fromTo(slashRef.current,
        { scaleX: 0, transformOrigin: 'left center' },
        { scaleX: 1, duration: 0.8, ease: 'power3.inOut' },
        0.4
      );

      /* 3. Radial glow pulses in */
      tl.fromTo(glowRef.current,
        { scale: 0.4, opacity: 0 },
        { scale: 1, opacity: 1, duration: 1.2, ease: 'power2.out' },
        0.6
      );

      /* 4. Content panel wipe in */
      tl.fromTo(contentRef.current,
        { clipPath: 'inset(0 100% 0 0)', opacity: 0 },
        { clipPath: 'inset(0 0% 0 0)', opacity: 1, duration: 1.1, ease: 'power3.inOut' },
        0.5
      );

      /* 5. Eyebrow badge */
      tl.fromTo(eyebrowRef.current,
        { y: -20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6 },
        0.9
      );

      /* 6. Accent line */
      tl.fromTo(lineRef.current,
        { scaleX: 0, transformOrigin: 'left' },
        { scaleX: 1, duration: 0.7, ease: 'power2.inOut' },
        1.0
      );

      /* 7. Heading chars flip in */
      const chars = headingRef.current?.querySelectorAll('.av2-char');
      if (chars?.length) {
        tl.fromTo(chars,
          { y: 90, rotationX: -75, opacity: 0 },
          { y: 0, rotationX: 0, opacity: 1, duration: 0.9, stagger: 0.03, ease: 'back.out(1.6)' },
          1.0
        );
      }

      /* 8. Sub text */
      tl.fromTo(subRef.current,
        { y: 22, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.7 },
        1.35
      );

      /* 9. Tags stagger */
      const tags = tagsRef.current?.querySelectorAll('.av2-tag');
      if (tags?.length) {
        tl.fromTo(tags,
          { scale: 0.8, opacity: 0 },
          { scale: 1, opacity: 1, duration: 0.5, stagger: 0.07, ease: 'back.out(2)' },
          1.45
        );
      }

      /* 10. Stats */
      const stats = statsRef.current?.querySelectorAll('.av2-stat');
      if (stats?.length) {
        tl.fromTo(stats,
          { y: 28, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.55, stagger: 0.1 },
          1.6
        );
      }

      /* 11. CTA */
      tl.fromTo(ctaRef.current,
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6 },
        1.8
      );

      /* ── Perpetual particle drift ── */
      particlesRef.current?.querySelectorAll('.av2-pdot').forEach(dot => {
        gsap.to(dot, {
          x: `random(-25, 25)`,
          y: `random(-25, 25)`,
          opacity: `random(0.08, 0.55)`,
          duration: `random(4, 9)`,
          repeat: -1,
          yoyo: true,
          ease: 'sine.inOut',
          delay: `random(0, 4)`,
        });
      });

      /* ── Glow breathe loop ── */
      gsap.to(glowRef.current, {
        scale: 1.12,
        opacity: 0.6,
        duration: 3,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
        delay: 2,
      });

      /* ── Slash shimmer loop ── */
      gsap.to(slashRef.current, {
        opacity: 0.55,
        duration: 2.5,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
        delay: 1.5,
      });

    }, containerRef);

    /* ── Mouse parallax (desktop) ── */
    const handleMouse = (e) => {
      if (window.innerWidth <= 768) return;
      const { clientX, clientY } = e;
      const { width, height } = containerRef.current.getBoundingClientRect();
      const xPct = (clientX / width  - 0.5) * 2;
      const yPct = (clientY / height - 0.5) * 2;
      gsap.to(videoWrapRef.current,  { x: xPct * 8,  y: yPct * 5,  duration: 1.4, ease: 'power2.out' });
      gsap.to('.av2-glow',           { x: xPct * 20, y: yPct * 14, duration: 1.6, ease: 'power2.out' });
      gsap.to('.av2-content-inner',  { x: xPct * -6, y: yPct * -4, duration: 1.2, ease: 'power2.out' });
    };

    const el = containerRef.current;
    el?.addEventListener('mousemove', handleMouse);
    return () => {
      el?.removeEventListener('mousemove', handleMouse);
      ctx.revert();
    };
  }, []);

  const headingText = ['Our Legal', 'Practice Areas'];

  return (
    <div ref={containerRef} className="av2-container">

      {/* ── Particles ── */}
      <div ref={particlesRef} className="av2-particles" aria-hidden="true">
        {Array.from({ length: 50 }).map((_, i) => (
          <div
            key={i}
            className="av2-pdot"
            style={{
              left:   `${Math.random() * 100}%`,
              top:    `${Math.random() * 100}%`,
              width:  `${Math.random() * 3 + 1}px`,
              height: `${Math.random() * 3 + 1}px`,
              opacity: 0.2,
            }}
          />
        ))}
      </div>

      {/* ── Radial glow ── */}
      <div ref={glowRef} className="av2-glow" aria-hidden="true" />

      {/* ── Video panel ── */}
      <div ref={videoWrapRef} className="av2-video-wrap">
        <video
          ref={videoRef}
          className="av2-video"
          playsInline muted autoPlay loop
        >
          <source src={videoFile} type="video/mp4" />
        </video>

        {/* Overlaid number */}
        <div className="av2-video-num" aria-hidden="true">25</div>

        {/* Video overlay gradient */}
        <div className="av2-video-overlay" />

        {/* Bottom label */}
        <div className="av2-video-label">
          <span className="av2-video-label-dot" />
          Years of Legal Excellence
        </div>

        {/* Corner brackets */}
        <div className="av2-corner av2-corner--tl"><span /><span /></div>
        <div className="av2-corner av2-corner--bl"><span /><span /></div>
      </div>

      {/* ── Diagonal slash ── */}
      <div ref={slashRef} className="av2-slash" aria-hidden="true" />

      {/* ── Content panel ── */}
      <div ref={contentRef} className="av2-content">
        <div className="av2-content-inner">

          {/* Eyebrow */}
          <div ref={eyebrowRef} className="av2-eyebrow">
            <span className="av2-eyebrow-dot" />
            <span className="av2-eyebrow-text">Practice Areas</span>
          </div>

          {/* Accent line */}
          <div ref={lineRef} className="av2-accent-line" />

          {/* Heading */}
          <h2 ref={headingRef} className="av2-heading" aria-label={headingText.join(' ')}>
            {headingText.map((line, li) => (
              <span key={li} className="av2-heading-line">
                {line.split('').map((ch, ci) => (
                  <span key={ci} className="av2-char">
                    {ch === ' ' ? '\u00A0' : ch}
                  </span>
                ))}
                {li === 0 && <br />}
              </span>
            ))}
          </h2>

          {/* Sub */}
          <p ref={subRef} className="av2-sub">
            Dedicated counsel for complex legal challenges. Precision,
            strategy, and relentless advocacy — from day one to verdict.
          </p>

          {/* Tags */}
          <div ref={tagsRef} className="av2-tags">
            {PRACTICE_AREAS.map((area) => (
              <span key={area.label} className="av2-tag">
                <span className="av2-tag-icon">{area.icon}</span>
                {area.label}
              </span>
            ))}
          </div>

          {/* Stats */}
          <div ref={statsRef} className="av2-stats">
            {STATS.map((s, i) => (
              <div key={i} className="av2-stat">
                <span className="av2-stat-num">{s.num}</span>
                <span className="av2-stat-label">{s.label}</span>
                {i < STATS.length - 1 && <div className="av2-stat-div" />}
              </div>
            ))}
          </div>

          {/* CTA */}
          <div ref={ctaRef} className="av2-cta">
            <Link to="/contactus">
              <button className="av2-btn av2-btn--primary">
                Schedule Consultation
                <span className="av2-btn-arrow">→</span>
              </button>
            </Link>
            <Link to="/practice-areas">
              <button className="av2-btn av2-btn--ghost">View All Areas</button>
            </Link>
          </div>

        </div>
      </div>

    </div>
  );
};

export default Areavideo;